import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  getDiagnosticOrgStructure,
  putOrgUnitValidation,
  putOrgUnitValidationBatch,
  type OrgValidationBatchUpdate,
  type OrgValidationNode,
  type OrgValidationResult,
  type OrgValidationTree,
  type OrgValidationUpdate,
} from "@/api/diagnostic-org-structure";
import { diagnosticKeys } from "@/hooks/useDiagnostic";

/**
 * A chave fica debaixo do prefixo do snapshot do Diagnóstico de propósito: uma
 * decisão gravada invalida `diagnosticKeys.snapshot(projectId)` para o tile mudar,
 * e o TanStack casa por prefixo, então a árvore é relida junto. Tile e tela não
 * ficam mostrando números diferentes.
 */
export const diagnosticOrgStructureKeys = {
  tree: (projectId: string) => [...diagnosticKeys.snapshot(projectId), "org-structure"] as const,
};

/**
 * A árvore da estrutura organizacional, para a tela de detalhe (#1342).
 *
 * É a segunda leitura de rede do Diagnóstico, ao lado do `useDiagnostic`: a
 * árvore não cabe no snapshot, que traz só o agregado do tile. Quem chama é o
 * invólucro do detalhe; o componente que desenha continua recebendo por props.
 */
export function useDiagnosticOrgStructure(projectId: string | undefined) {
  return useQuery({
    queryKey: diagnosticOrgStructureKeys.tree(projectId ?? ""),
    queryFn: () => getDiagnosticOrgStructure(projectId!),
    enabled: !!projectId,
    staleTime: 30_000,
  });
}

/**
 * Grava a validação de uma unidade (Confere?, escopo, observação).
 *
 * Sem atualização otimista: a propagação do "fora do escopo" e a volta a
 * Pendente são do servidor, e a tela mostra o que ele gravou.
 *
 * Quando a decisão de escopo muda, invalida o snapshot do Diagnóstico: a árvore
 * mora debaixo da mesma chave e é relida junto (a herança muda), e o tile
 * "Empresas" muda sem esperar os 30 s do cache. Quando só a observação ou o
 * Confere? mudam, nada disso muda: a unidade é atualizada no cache com o que o
 * servidor gravou, sem reler a árvore inteira a cada clique. No erro, relê tudo:
 * 404 e 409 querem dizer que a tela estava desatualizada.
 */
export function useUpdateOrgValidation(projectId: string | undefined) {
  const client = useQueryClient();
  const refetchAll = () =>
    client.invalidateQueries({ queryKey: diagnosticKeys.snapshot(projectId ?? "") });

  return useMutation({
    mutationFn: (body: OrgValidationUpdate) => putOrgUnitValidation(projectId!, body),
    onSuccess: (result, body) => {
      if ("decision" in body) return refetchAll();
      client.setQueryData<OrgValidationTree>(
        diagnosticOrgStructureKeys.tree(projectId ?? ""),
        (tree) => tree && withSavedValidation(tree, body, result),
      );
    },
    onError: () => refetchAll(),
  });
}

/** A árvore com a validação gravada de uma unidade, no lugar da que estava. */
function withSavedValidation(
  tree: OrgValidationTree,
  body: OrgValidationUpdate,
  result: OrgValidationResult,
): OrgValidationTree {
  const saved = result.validation as Partial<OrgValidationNode["validation"]>;
  return {
    ...tree,
    nodes: tree.nodes.map((node) =>
      node.item_type === body.item_type && node.item_key === body.item_key
        ? {
            ...node,
            validation: {
              data_matches: saved.data_matches ?? null,
              decision: saved.decision ?? null,
              note: saved.note ?? null,
              decided_by: saved.decided_by ?? null,
              decided_by_name: saved.decided_by_name ?? null,
              decided_at: saved.decided_at ?? null,
            },
          }
        : node,
    ),
  };
}

/**
 * Grava a decisão de escopo de várias unidades de uma vez (spec 2026-10-23).
 *
 * Relê o snapshot do Diagnóstico no sucesso e no erro, como o PUT de uma
 * unidade com decisão: a herança muda, e 404 e 409 dizem que a tela estava
 * desatualizada. `mutateAsync` só termina depois da releitura.
 */
export function useBatchOrgValidation(projectId: string | undefined) {
  const client = useQueryClient();
  const refetchAll = () =>
    client.invalidateQueries({ queryKey: diagnosticKeys.snapshot(projectId ?? "") });

  return useMutation({
    mutationFn: (body: OrgValidationBatchUpdate) => putOrgUnitValidationBatch(projectId!, body),
    onSuccess: () => refetchAll(),
    onError: () => refetchAll(),
  });
}
